import { ConstitutionSnapshot, SectionKey } from '../models/constitution-snapshot.model';

// ── Category & Subtype types ───────────────────────────────────────────────

export type OperacionCategoria =
  | 'constitucion'
  | 'modificacion_estatutos'
  | 'modificacion_organo_admin'
  | 'compraventa_transmision'
  | 'reestructuracion'
  | 'transformacion'
  | 'disolucion_liquidacion'
  | 'escrituras_poderes';

export type OperacionSubtipo =
  // Modificación de estatutos
  | 'denominacion_social'
  | 'domicilio_social'
  | 'objeto_social'
  | 'ampliacion_capital'
  | 'reduccion_capital'
  | 'operacion_acordeon'
  | 'modificacion_serie'
  | 'contrasplit'
  | 'articulado_estatutos'
  // Modificación órgano de administración
  | 'alta_baja_cambio_admin'
  // Compraventa o transmisión de acciones
  | 'compraventa_donacion_herencia'
  // Reestructuración
  | 'fusion_absorbente'
  | 'fusion_absorbida'
  | 'fusion_socios_filial'
  | 'escision'
  // Transformación
  | 'cambio_forma_juridica'
  // Escrituras de poderes
  | 'alta_baja_apoderados';

// ── Config interfaces ──────────────────────────────────────────────────────

export interface SubtipoConfig {
  value: OperacionSubtipo;
  label: string;
  descripcion: string;
  seccionesAfectadas: SectionKey[];
  /** Tab outside the constitution form that this subtype affects, for info display */
  tabAfectada?: { key: string; label: string; accion: string };
}

export interface CategoriaConfig {
  value: OperacionCategoria;
  label: string;
  /** If true, no subtype selection is needed */
  sinSubtipos?: boolean;
  descripcion?: string;
  seccionesAfectadas?: SectionKey[];
  tabAfectada?: { key: string; label: string; accion: string };
  subtipos: SubtipoConfig[];
}

// ── Full category + subtype hierarchy ─────────────────────────────────────

export const CATEGORIAS: CategoriaConfig[] = [
  {
    value: 'constitucion',
    label: 'Constitución',
    sinSubtipos: true,
    descripcion: 'Primera operación de la línea temporal (obligatoria). Define el estado inicial de la sociedad.',
    seccionesAfectadas: ['objetoSocial', 'capitalSocial', 'socios', 'administracion', 'apoderados'],
    subtipos: [],
  },
  {
    value: 'modificacion_estatutos',
    label: 'Modificación de estatutos',
    subtipos: [
      {
        value: 'denominacion_social',
        label: 'Denominación social',
        descripcion: 'Modifica la razón social de la empresa. El nuevo nombre quedará reflejado en la pestaña Identificación del cliente.',
        seccionesAfectadas: [],
        tabAfectada: { key: 'identification', label: 'Identificación', accion: 'Cambia el campo "Razón social"' },
      },
      {
        value: 'domicilio_social',
        label: 'Domicilio social',
        descripcion: 'Registra un cambio en el domicilio social de la empresa. Se añadirá un nuevo domicilio en la pestaña Domicilios del cliente.',
        seccionesAfectadas: [],
        tabAfectada: { key: 'addresses', label: 'Domicilios', accion: 'Añade un nuevo domicilio de tipo "Social"' },
      },
      {
        value: 'objeto_social',
        label: 'Objeto social',
        descripcion: 'Modifica el objeto social y/o código CNAE de la sociedad.',
        seccionesAfectadas: ['objetoSocial'],
      },
      {
        value: 'ampliacion_capital',
        label: 'Ampliación de capital social',
        descripcion: 'Aumenta el capital social mediante la emisión de nuevas participaciones.',
        seccionesAfectadas: ['capitalSocial', 'socios'],
      },
      {
        value: 'reduccion_capital',
        label: 'Reducción de capital social',
        descripcion: 'Disminuye el capital social mediante amortización o reducción del valor nominal de participaciones.',
        seccionesAfectadas: ['capitalSocial', 'socios'],
      },
      {
        value: 'operacion_acordeon',
        label: 'Operación acordeón',
        descripcion: 'Operación simultánea de reducción y ampliación de capital para sanear pérdidas.',
        seccionesAfectadas: ['capitalSocial', 'socios'],
      },
      {
        value: 'modificacion_serie',
        label: 'Modificación serie',
        descripcion: 'Modifica los parámetros de una serie de participaciones (numeración, valor nominal, etc.).',
        seccionesAfectadas: ['capitalSocial', 'socios'],
      },
      {
        value: 'contrasplit',
        label: 'Contrasplit',
        descripcion: 'Agrupación de participaciones para reducir su número total manteniendo el capital.',
        seccionesAfectadas: ['capitalSocial', 'socios'],
      },
      {
        value: 'articulado_estatutos',
        label: 'Articulado estatutos',
        descripcion: 'Modificación de artículos concretos del articulado de los estatutos sociales.',
        seccionesAfectadas: [],
      },
    ],
  },
  {
    value: 'modificacion_organo_admin',
    label: 'Modificación órgano de administración',
    subtipos: [
      {
        value: 'alta_baja_cambio_admin',
        label: 'Alta / baja / cambio de administradores',
        descripcion: 'Registra altas, bajas o cambios en los miembros del órgano de administración.',
        seccionesAfectadas: ['administracion'],
      },
    ],
  },
  {
    value: 'compraventa_transmision',
    label: 'Compraventa o transmisión acciones',
    subtipos: [
      {
        value: 'compraventa_donacion_herencia',
        label: 'Compraventa, donación, herencia, permuta, canje de valores',
        descripcion: 'Transmisión de participaciones entre socios o a terceros por cualquier título.',
        seccionesAfectadas: ['socios'],
      },
    ],
  },
  {
    value: 'reestructuracion',
    label: 'Reestructuración',
    subtipos: [
      {
        value: 'fusion_absorbente',
        label: 'Fusión absorbente',
        descripcion: 'La sociedad actúa como entidad absorbente en un proceso de fusión.',
        seccionesAfectadas: ['capitalSocial', 'socios'],
      },
      {
        value: 'fusion_absorbida',
        label: 'Fusión absorbida',
        descripcion: 'La sociedad actúa como entidad absorbida y queda extinguida en el proceso de fusión.',
        seccionesAfectadas: [],
      },
      {
        value: 'fusion_socios_filial',
        label: 'Fusión socios filial',
        descripcion: 'Fusión por absorción de filial de la que es socio único.',
        seccionesAfectadas: ['socios'],
      },
      {
        value: 'escision',
        label: 'Escisión',
        descripcion: 'División del patrimonio de la sociedad para crear una o más sociedades nuevas.',
        seccionesAfectadas: ['capitalSocial', 'socios'],
      },
    ],
  },
  {
    value: 'transformacion',
    label: 'Transformación',
    subtipos: [
      {
        value: 'cambio_forma_juridica',
        label: 'Cambio de forma jurídica',
        descripcion: 'Modifica la forma jurídica de la sociedad (p.ej. de SL a SA o viceversa).',
        seccionesAfectadas: [],
        tabAfectada: {
          key: 'identification',
          label: 'Identificación',
          accion: 'Modifica los datos generales y documentos de identificación',
        },
      },
    ],
  },
  {
    value: 'disolucion_liquidacion',
    label: 'Disolución y liquidación',
    sinSubtipos: true,
    descripcion: 'Inicio del proceso de disolución y liquidación de la sociedad.',
    seccionesAfectadas: [],
    subtipos: [],
  },
  {
    value: 'escrituras_poderes',
    label: 'Escritura poderes',
    subtipos: [
      {
        value: 'alta_baja_apoderados',
        label: 'Alta / baja de apoderados',
        descripcion: 'Registra el otorgamiento o revocación de poderes notariales a personas físicas o jurídicas.',
        seccionesAfectadas: ['apoderados'],
      },
    ],
  },
];

// ── Helper lookups ─────────────────────────────────────────────────────────

export function getCategoriaConfig(cat: OperacionCategoria): CategoriaConfig | undefined {
  return CATEGORIAS.find(c => c.value === cat);
}

export function getSubtipoConfig(subtipo: OperacionSubtipo): SubtipoConfig | undefined {
  for (const cat of CATEGORIAS) {
    const found = cat.subtipos.find(s => s.value === subtipo);
    if (found) return found;
  }
  return undefined;
}

export function getSeccionesAfectadas(
  categoria: OperacionCategoria,
  subtipo: OperacionSubtipo | null
): SectionKey[] {
  if (subtipo) {
    return getSubtipoConfig(subtipo)?.seccionesAfectadas ?? [];
  }
  return getCategoriaConfig(categoria)?.seccionesAfectadas ?? [];
}

// ── Legacy OperacionTipo kept for backward compat (still used in wizard) ──

export type OperacionTipo = OperacionCategoria; // alias

// ── OperacionSocietaria ────────────────────────────────────────────────────

export interface OperacionSocietaria {
  id: number;
  categoria: OperacionCategoria;
  subtipo: OperacionSubtipo | null;
  categoriaLabel: string;
  subtipoLabel: string;
  /** Display label for the operation (categoria + subtipo combined) */
  tipoLabel: string;
  fechaEscritura: string;
  notario: string;
  protocolo: string;
  irus?: string;
  observaciones?: string;
  concepto?: string;
  provinciaRM?: string;
  fechaInscripcion?: string;
  tomo?: string;
  folio?: string;
  hoja?: string;
  seccion?: string;
  libro?: string;
  numInscripcion?: string;
  documentos?: import('../../../../core/models/corporate-operation.model').CorporateOperationFile[];
  snapshot: ConstitutionSnapshot;
  seccionesAfectadas: SectionKey[];
  operationData?: Record<string, unknown> | null;
  createdAt?: string;
  /** Local midnight of the deed day. Used to sort and to filter the histórico. */
  deedTime?: number;
}

// ── Shared base snapshot ───────────────────────────────────────────────────

const SNAPSHOT_CONSTITUCION: ConstitutionSnapshot = {
  escritura: {
    fecha: '22/12/2025',
    concepto: 'Constitución de sociedad',
    notario: 'PRUEBA',
    protocolo: '123',
    provinciaRM: 'Barcelona',
    fechaInscripcion: '15/01/2026',
    tomo: '47521',
    folio: '104',
    hoja: 'B-512300',
    seccion: '8ª',
    libro: '0',
    numInscripcion: '1ª',
  },
  objetoSocial: {
    codigoCnae: '6499',
    descripcion:
      'Compraventa, importación y exportación de bienes y servicios de toda índole. Asesoramiento empresarial, financiero y fiscal.',
  },
  capitalSocial: {
    hasSeries: false,
    totalCapital: '3.000,00 €',
    totalAcciones: 300,
    acccionesDesde: 1,
    accionesHasta: 300,
    valorNominal: 10,
    series: [],
  },
  socios: [
    {
      nombre: 'HOLDINGS TOP SL',
      nif: 'B12345678',
      numAcciones: 180,
      titulo: 'Plena propiedad',
      porcentajeParticipacion: '60,00 %',
      esPresidente: true,
      esSecretario: false,
    },
    {
      nombre: 'INVERSIONES PONS SL',
      nif: 'B87654321',
      numAcciones: 120,
      titulo: 'Plena propiedad',
      porcentajeParticipacion: '40,00 %',
      esPresidente: false,
      esSecretario: true,
    },
  ],
  administracion: {
    sistema: 'Administrador Único',
    miembros: [
      {
        nombre: 'JORDI PONS VILA',
        nif: '46302907X',
        cargo: 'Administrador Único',
        fechaAlta: '22/12/2025',
      },
    ],
  },
  apoderados: [],
};

const SNAPSHOT_AMPLIACION: ConstitutionSnapshot = {
  ...SNAPSHOT_CONSTITUCION,
  escritura: {
    ...SNAPSHOT_CONSTITUCION.escritura,
    fecha: '14/03/2026',
    concepto: 'Ampliación de capital social',
    protocolo: '87',
    fechaInscripcion: '02/04/2026',
    numInscripcion: '2ª',
  },
  capitalSocial: {
    hasSeries: true,
    totalCapital: '15.000,00 €',
    totalAcciones: 1500,
    series: [
      { nombre: 'Serie A', totalAcciones: 300, valorNominal: 10, desde: 1, hasta: 300, capitalSocial: 3000 },
      { nombre: 'Serie B', totalAcciones: 1200, valorNominal: 10, desde: 301, hasta: 1500, capitalSocial: 12000 },
    ],
  },
  socios: [
    { nombre: 'HOLDINGS TOP SL', nif: 'B12345678', numAcciones: 900, titulo: 'Plena propiedad', porcentajeParticipacion: '60,00 %', esPresidente: true, esSecretario: false },
    { nombre: 'INVERSIONES PONS SL', nif: 'B87654321', numAcciones: 600, titulo: 'Plena propiedad', porcentajeParticipacion: '40,00 %', esPresidente: false, esSecretario: true },
  ],
};

const SNAPSHOT_DENOMINACION: ConstitutionSnapshot = {
  ...SNAPSHOT_AMPLIACION,
  escritura: {
    ...SNAPSHOT_AMPLIACION.escritura,
    fecha: '05/07/2026',
    concepto: 'Modificación de estatutos: denominación social',
    protocolo: '112',
    fechaInscripcion: '22/07/2026',
    numInscripcion: '3ª',
  },
  apoderados: [
    { nombre: 'MARTA GARCIA SOLER', nif: '46302907C', tipoPoder: 'Poder general', fechaAlta: '05/07/2026' },
  ],
};

const SNAPSHOT_PODERES: ConstitutionSnapshot = {
  ...SNAPSHOT_DENOMINACION,
  escritura: {
    ...SNAPSHOT_DENOMINACION.escritura,
    fecha: '15/07/2026',
    concepto: 'Otorgamiento de poder notarial',
    protocolo: '134',
    fechaInscripcion: '',
    numInscripcion: '',
  },
  apoderados: [
    { nombre: 'MARTA GARCIA SOLER', nif: '46302907C', tipoPoder: 'Poder general', fechaAlta: '05/07/2026' },
    { nombre: 'RAMON VIDAL COSTA', nif: '38291047K', tipoPoder: 'Poder especial — contratos bancarios', fechaAlta: '15/07/2026' },
  ],
};

export const MOCK_ESTADO_ACTUAL: ConstitutionSnapshot = SNAPSHOT_PODERES;

export const MOCK_OPERACIONES: OperacionSocietaria[] = [
  {
    id: 1,
    categoria: 'constitucion',
    subtipo: null,
    categoriaLabel: 'Constitución',
    subtipoLabel: '',
    tipoLabel: 'Constitución',
    fechaEscritura: '22/12/2025',
    notario: 'PRUEBA',
    protocolo: '123',
    irus: 'IRUS-2025-0041',
    observaciones: 'Constitución inicial de la sociedad.',
    snapshot: SNAPSHOT_CONSTITUCION,
    seccionesAfectadas: getSeccionesAfectadas('constitucion', null),
  },
  {
    id: 2,
    categoria: 'modificacion_estatutos',
    subtipo: 'ampliacion_capital',
    categoriaLabel: 'Modificación de estatutos',
    subtipoLabel: 'Ampliación de capital social',
    tipoLabel: 'Modificación de estatutos: Ampliación de capital social',
    fechaEscritura: '14/03/2026',
    notario: 'Pons',
    protocolo: '87',
    irus: 'IRUS-2026-0201',
    observaciones: 'Ampliación aprobada en junta extraordinaria del 28/02/2026.',
    snapshot: SNAPSHOT_AMPLIACION,
    seccionesAfectadas: getSeccionesAfectadas('modificacion_estatutos', 'ampliacion_capital'),
    operationData: {
      tipoAmpliacion: 'dineraria',
      modoAmpliacion: 'ampliacion_acciones',
      accNumAmpliado: '1200',
      accNuevasAcciones: '1200',
      accDesde: '301',
      accHasta: '1500',
    },
  },
  {
    id: 3,
    categoria: 'modificacion_estatutos',
    subtipo: 'denominacion_social',
    categoriaLabel: 'Modificación de estatutos',
    subtipoLabel: 'Denominación social',
    tipoLabel: 'Modificación de estatutos: Denominación social',
    fechaEscritura: '05/07/2026',
    notario: 'Pons',
    protocolo: '112',
    irus: 'IRUS-2026-0341',
    observaciones: 'Cambio aprobado en junta del 20/06/2026.',
    snapshot: SNAPSHOT_DENOMINACION,
    seccionesAfectadas: getSeccionesAfectadas('modificacion_estatutos', 'denominacion_social'),
  },
  {
    id: 4,
    categoria: 'escrituras_poderes',
    subtipo: 'alta_baja_apoderados',
    categoriaLabel: 'Escritura poderes',
    subtipoLabel: 'Alta / baja de apoderados',
    tipoLabel: 'Escritura poderes: Alta / baja de apoderados',
    fechaEscritura: '15/07/2026',
    notario: 'Pons',
    protocolo: '134',
    irus: 'IRUS-2026-0388',
    observaciones: 'Alta de nuevo apoderado: Ramón Vidal Costa (poder especial contratos bancarios).',
    snapshot: SNAPSHOT_PODERES,
    seccionesAfectadas: getSeccionesAfectadas('escrituras_poderes', 'alta_baja_apoderados'),
  },
];
